/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { FeatureFlag } from "constants/features"

/**
 * Store mode: enableBCRS is a per-merchant flag delivered on merchant.featureFlags
 * (from food-max-api), living in Redux under menu.outletV2 — read it from there so
 * it resets cleanly on merchant switch (no cross-merchant leak).
 *
 * Menu Group V2 mode: the menu-group page never fetches merchant detail, so outletV2
 * is empty. Instead we gate on `enableBcrsMenuGroupV2`, resolved directly from GrabX
 * (with country/merchant facets) in MenuEditor.fetchMenuFeatureFlag — mirroring how
 * the alcohol/tobacco MenuGroupV2 flags are handled.
 *
 * The mode flag is `isMenuGroupPage`, written by MenuEditor from its own
 * `menuGroupID` (i.e. `isMenuGroup && !isInventory`) and reset by CLEAR_MENU.
 * Redux `isMenuGroup` must not be used here: it is a merchant-group-level setting
 * (FETCH_STORE_LIST_SUCCESS) that is also true on the inventory pages of a
 * menu-group-enabled merchant group, and those pages do populate outletV2, so they
 * have to keep reading the store flag.
 */
export const useIsBcrsEnabled = (): boolean => {
  const { outletV2, isMenuGroupPage, enableBcrsMenuGroupV2 } = useSelector(
    (state: AppState) => state?.menu,
  )

  if (isMenuGroupPage) return !!enableBcrsMenuGroupV2
  return !!outletV2?.featureFlags?.[FeatureFlag.enableBCRS]
}
